import type { AxiosError, AxiosRequestConfig } from 'axios';

import axios from 'axios';
import i18next from 'i18next';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/config-global';

import { toast } from 'src/components/snackbar';
import {
  setLastTraceId,
  getNewRelicTraceId,
  getNewRelicSessionId,
} from 'src/components/new-relic/utils';
import { logger } from 'src/lib/observability/logger';

const UNAUTHENTICATED_ERROR_CODE = 401;
const FORBIDDEN_ERROR_CODE = 403;
// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.site.serverUrl });

axiosInstance.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  (config.headers as any)['Accept-Language'] = i18next.language || 'en';

  (config.headers as any)['x-session-id'] = getNewRelicSessionId();

  if (typeof window !== 'undefined' && !(config.headers as any).Authorization) {
    const accessToken = localStorage.getItem('token');
    if (accessToken) {
      (config.headers as any).Authorization = `Bearer ${accessToken}`;
    }
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    setLastTraceId(getNewRelicTraceId(response.request));
    return response;
  },
  (error) => {
    setLastTraceId(getNewRelicTraceId(error.request));
    const hasResponse = !!error.response; // server replied
    const hasRequest = !!error.request; // request made but no response (network error)

    if (error.response?.status >= 500 || !hasResponse || !hasRequest) {
      logger.error('API Request Failed', {
        isUnexpectedError: true,
        // High-level classification
        category: hasResponse ? 'http_error': hasRequest ? 'network_error' : 'client_error',

        // Axios basics
        errorMessage: error.message,
        code: error.code, // ECONNABORTED, ENOTFOUND, etc.

        // Request details
        url: error.config?.url,
        method: error.config?.method,
        timeout: error.config?.timeout,
        baseURL: error.config?.baseURL,

        // Response details (if any)
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseHeaders: error.response?.headers,
        responseBody: error.response?.data,

        // Tracing
        'trace.id': getNewRelicTraceId(error.request),

        // // Raw error (last resort, careful in prod)
        // stack: error.stack,
      });
    }

    if (error.response?.status === UNAUTHENTICATED_ERROR_CODE) {
      const reauthenticate = error.config?.reauthenticate !== false;
      if (reauthenticate) {
        const currentPath = window.location.pathname;
        // refresh token - get from localStorage
        const refreshToken = localStorage.getItem('refreshToken');
        const user = localStorage.getItem('user');
        let parsedUser = null;
        try {
          parsedUser = user ? JSON.parse(user) : null;
        } catch {
          parsedUser = null;
        }
        const isRefreshFailure = error.config.url === endpoints.auth.refreshToken;
        if (refreshToken && parsedUser && !isRefreshFailure) {
          return axiosInstance
            .put(
              endpoints.auth.refreshToken,
              {
                id: parsedUser.id,
                refreshToken,
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${refreshToken}`,
                },
              }
            )
            .then((res) => {
              // Use the setSession function to properly store tokens
              const { setSession } = require('src/auth/context/jwt/utils');
              if (!res.data?.accessToken) {
                return Promise.reject(
                  res.data || new Error('Refresh did not return an access token')
                );
              }
              setSession(res.data.accessToken, res.data.refreshToken, res.data.user);
              error.config.headers.Authorization = `Bearer ${res.data.accessToken}`;
              error.config.baseURL = undefined;
              return axiosInstance.request(error.config);
            })
            .catch((err) => {
              return Promise.reject(
                err.response?.data ||
                  err ||
                  new Error(i18next.t('errors.somethingWentWrong', { ns: 'common' }))
              );
            });
        }
        // Only bounce to sign-in when the stored access token is actually gone
        // or expired. A 401 from one endpoint (MFA policy, missing header, etc.)
        // must not log out a still-valid session — that fights AuthGuard/GuestGuard.
        if (!currentPath.startsWith('/auth/')) {
          const accessToken = localStorage.getItem('token');
          const { isValidToken } = require('src/auth/context/jwt/utils');
          if (!accessToken || !isValidToken(accessToken)) {
            const returnTo = currentPath === '/' ? '' : `?returnTo=${currentPath}`;
            window.location.href = `${paths.auth.jwt.signIn}${returnTo}`;
          }
        }
      }
    }
    if (error.response?.status === FORBIDDEN_ERROR_CODE) {
      const storedUser = localStorage.getItem('user');
      let parsedStoredUser = null;
      try {
        parsedStoredUser = storedUser ? JSON.parse(storedUser) : null;
      } catch {
        parsedStoredUser = null;
      }
      const isCustomOrgRole = Array.isArray(parsedStoredUser?.roles) && parsedStoredUser.roles.includes('custom');

      if (!isCustomOrgRole) {
        window.location.href = paths.page403;
      }
      toast.error(i18next.t('errors.forbidden', { ns: 'common' }));
    }
   
    const rejected = error.response?.data || new Error(i18next.t('errors.somethingWentWrong', { ns: 'common' }));
    return Promise.reject(
      rejected instanceof Error
        ? Object.assign(rejected, { status: error.response?.status })
        : { ...rejected, status: error.response?.status }
    );
  }
);

export default axiosInstance;

/**
 * Human-readable message from a request rejected by the interceptor above.
 * The rejected value is the API body (Nest `message` is a string or a
 * string[] for validation errors) or an Error.
 */
export function getApiErrorMessage(error: unknown): string | undefined {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (Array.isArray(message)) return message.join(', ') || undefined;
  return typeof message === 'string' && message ? message : undefined;
}

// ----------------------------------------------------------------------

export const fetcher = async <T>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
  config?: Omit<AxiosRequestConfig, 'url' | 'method'>
): Promise<T> => {
  try {
    const headers = {
      ...config?.headers,
    };

    const res = await axiosInstance.request({
      url,
      method,
      ...config,
      headers,
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: {
    root: '/api/v1/calendar',
    status: '/api/v1/calendar/calendar-status',
    login: '/api/v1/calendar/auth/microsoft/login',
    disconnect: '/api/v1/calendar/subscription',
  },
  auth: {
    // me: '/api/auth/me',
    me: '/api/v1/auth/me',

    // signIn: '/api/auth/sign-in',
    signIn: '/api/v1/auth/login',

    // signUp: '/api/auth/sign-up',
    signUp: '/api/v1/auth/signup',
    // /api/v1/auth/email-verification?token=CKRYXX
    verify: '/api/v1/auth/email-verification',
    resendVerification: '/api/v1/auth/resend-verification',

    createCompanyName: '/api/v1/clients/createCompanyName',

    passwordResetEmail: '/api/v1/auth/password/reset',

    passwordReset: (token: string) => `/api/v1/auth/password/reset/${token}`,

    updatePassword: (userId: number | string) => `/api/v1/auth/password/${userId}`,

    acceptInvitation: (token: string) => `/api/v1/auth/invitation/accept/${token}`,
    validateInvitation: (token: string) =>
      `/api/v1/auth/invitation/validate/${encodeURIComponent(token)}`,
    refreshToken: '/api/v1/auth/refresh',
    logout: '/api/v1/auth/logout',
    verifyLoginTwoFactor: '/api/v1/auth/login/verify-2fa',
    completeLoginMfaEnrollment: '/api/v1/auth/login/mfa-enrollment/complete',
    startLoginTotpEnrollment: '/api/v1/auth/login/mfa-enrollment/totp/start',
    confirmLoginTotpEnrollment: '/api/v1/auth/login/mfa-enrollment/totp/confirm',
    startUserTotpEnrollment: '/api/v1/auth/mfa/totp/enroll/start',
    confirmUserTotpEnrollment: '/api/v1/auth/mfa/totp/enroll/confirm',
    ssoDiscover: '/api/v1/auth/sso/discover',
    ssoComplete: (providerId: string) => `/api/v1/auth/sso/${providerId}/complete`,
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    // list: '/api/product/list',
    list: '/api/v1/product/list',

    // details: '/api/product/details',
    details: '/api/v1/product/details',

    // search: '/api/product/search',
    search: '/api/v1/product/search',
  },
  scheme: {
    root: `/api/v1/standard-template`,
    list: '/api/v1/standard-template/list',
    details: '/api/v1/standard-template',
    // search: '/api/v1/schemes/search',
    create: '/api/v1/standard-template',
    update: '/api/v1/standard-template',
    delete: '/api/v1/standard-template',
    names: '/api/v1/standard-template/names',
    competencies: (standardId: number) => `/api/v1/standard-template/${standardId}/competencies`,
    accreditationBodies: (standardId: number | string) => `/api/v1/standard-template/${standardId}/accreditation-bodies`,
    rotationRules: {
      get: (schemeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/rotation-rules`,
      create: (schemeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/rotation-rules`,
      update: (schemeId: number | string, ruleId: number | string) =>
        `/api/v1/standard-template/${schemeId}/rotation-rules/${ruleId}`,
      delete: (schemeId: number | string, ruleId: number | string) =>
        `/api/v1/standard-template/${schemeId}/rotation-rules/${ruleId}`,
    },
  },

  // customer: {
  //   list: '/api/v1/customers',
  //   // details: '/api/v1/standard-template',
  //   // // search: '/api/v1/schemes/search',
  //   // create: '/api/v1/standard-template',
  //   // update: '/api/v1/standard-template',
  //   // delete: '/api/v1/standard-template',
  // },
  activityTemplates: {
    list: '/api/v1/activity-template/list',
    details: '/api/v1/activity-template',
    search: '/api/v1/activity-template/search',
    create: '/api/v1/activity-template',
    update: '/api/v1/activity-template',
    delete: '/api/v1/activity-template',
    competencies: (activityTemplateId: number) =>
      `/api/v1/activity-template/${activityTemplateId}/competencies`,
    root: '/api/v1/activity-template',
  },
  competency: {
    root: `/api/v1/competencies`,
    list: '/api/v1/competencies/list',
    hierarchy: '/api/v1/competencies/hierarchy',
    details: '/api/v1/competencies',
    search: '/api/v1/competencies/search',
    create: '/api/v1/competencies',
    update: '/api/v1/competencies',
    delete: '/api/v1/competencies',
    names: '/api/v1/competencies/names',
  },
  competencyCategories: {
    list: '/api/v1/competency-categories',
    details: '/api/v1/competency-categories',
    search: '/api/v1/competency-categories/search',
    create: '/api/v1/competency-categories',
    update: '/api/v1/competency-categories',
    delete: '/api/v1/competency-categories',
  },
  createdCompetenciesCategory: {
    list: '/api/v1/created-competencies-categories',
    details: '/api/v1/created-competencies-categories',
    search: '/api/v1/created-competencies-categories/search',
    create: '/api/v1/created-competencies-categories',
    update: '/api/v1/created-competencies-categories',
    delete: '/api/v1/created-competencies-categories',
  },
  customer: {
    root: `/api/v1/customers`,
    list: '/api/v1/customers/list',
    details: '/api/v1/customers',
    search: '/api/v1/customers/search',
    create: '/api/v1/customers',
    update: '/api/v1/customers',
    delete: '/api/v1/customers',
    names: '/api/v1/customers/names',
    countries: '/api/v1/customers/countries',
  },
  contactPeople: {
    root: '/api/v1/contact-people',
    list: '/api/v1/contact-people',
    details: (id: number | string) => `/api/v1/contact-people/${id}`,
    create: '/api/v1/contact-people',
    update: (id: number | string) => `/api/v1/contact-people/${id}`,
    delete: (id: number | string) => `/api/v1/contact-people/${id}`,
  },
  customerCategories: {
    list: '/api/v1/customer-categories',
    details: '/api/v1/customer-categories',
    search: '/api/v1/customer-categories/search',
    create: '/api/v1/customer-categories',
    update: '/api/v1/customer-categories',
    delete: '/api/v1/customer-categories',
  },
  customerSchemes: {
    root: '/api/v1/customer-schemes',
    list: '/api/v1/customer-schemes/list',
    listByCustomerID: (customerId: number | string) =>
      `/api/v1/customer-schemes/customer/${customerId}`,
    details: (id: number | string) => `/api/v1/customer-schemes/${id}`,
    create: '/api/v1/customer-schemes',
    update: (id: number | string) => `/api/v1/customer-schemes/${id}`,
    delete: (id: number | string) => `/api/v1/customer-schemes/${id}`,
  },
  accreditationBodies: {
    list: '/api/v1/accreditation-bodies',
    details: '/api/v1/accreditation-bodies',
    create: '/api/v1/accreditation-bodies',
    update: '/api/v1/accreditation-bodies',
    delete: '/api/v1/accreditation-bodies',
  },
  site: {
    root: `/api/v1/sites`,
    list: '/api/v1/sites/list',
    listByCustomerID: '/api/v1/sites/customer',
    customer: '/api/v1/sites/customer',
    details: '/api/v1/sites',
    search: '/api/v1/sites/search',
    create: '/api/v1/sites',
    update: '/api/v1/sites',
    delete: '/api/v1/sites',
    contactPeople: {
      list: (siteId: number | string) => `/api/v1/sites/${siteId}/contact-people`,
      details: (siteId: number | string, contactId: number | string) =>
        `/api/v1/sites/${siteId}/contact-people/${contactId}`,
      create: (siteId: number | string) => `/api/v1/sites/${siteId}/contact-people`,
      update: (siteId: number | string, contactId: number | string) =>
        `/api/v1/sites/${siteId}/contact-people/${contactId}`,
      delete: (siteId: number | string, contactId: number | string) =>
        `/api/v1/sites/${siteId}/contact-people/${contactId}`,
    },
  },

  recurringschedules: {
    list: '/api/v1/recurring-schedules/list',
    listByCustomerID: '/api/v1/recurring-schedules/customer',
    details: '/api/v1/recurring-schedules',
    search: '/api/v1/recurring-schedules/search',
    create: '/api/v1/recurring-schedules',
    update: '/api/v1/recurring-schedules',
    delete: '/api/v1/recurring-schedules',
  },

  projects: {
    root: '/api/v1/projects',
    list: '/api/v1/projects/list',
    listByCustomerID: '/api/v1/projects/customer',
    details: '/api/v1/projects',
    search: '/api/v1/projects/search',
    create: '/api/v1/projects',
    update: (id: number | string) => `/api/v1/projects/${id}`,
    changeOwner: (id: number | string) => `/api/v1/projects/${id}/owner`,
    delete: '/api/v1/projects',
    names: '/api/v1/projects/names',
    eligibleOwners: '/api/v1/projects/eligible-owners',
    followUpData: (id: number | string) => `/api/v1/projects/${id}/follow-up-data`,
    copyData: (id: number | string) => `/api/v1/projects/${id}/copy-data`,
    copy: (id: number | string) => `/api/v1/projects/${id}/copy`,
    projectStandards: (id: number | string) => `/api/v1/projects/${id}/standards`,
    updateProjectStandard: (id: number | string, standardId: number | string) =>
      `/api/v1/projects/${id}/standards/${standardId}`,
    teams: (id: number | string) => `/api/v1/projects/${id}/teams`,
    inspectorsAllocated: (id: number | string) => `/api/v1/projects/${id}/inspectors`,
    upload: (projectId?: number | string) => `/api/v1/projects/${projectId}/files`,
    getFiles: (projectId?: number | string) => `/api/v1/projects/${projectId}/files`,
    getAttachments: (projectId?: number | string) => `/api/v1/projects/${projectId}/attachments`,
    getFileDownloadLink: (projectId?: number | string, fileId?: number | string) =>
      `/api/v1/projects/${projectId}/files/${fileId}`,
    deleteFile: (projectId?: number | string, fileId?: number | string) =>
      `/api/v1/projects/${projectId}/files/${fileId}`,
    deleteLink: (projectId?: number | string, linkId?: number | string) =>
      `/api/v1/projects/${projectId}/links/${linkId}`,
    updateFileStatus: (projectId?: number | string) => `/api/v1/projects/${projectId}/files`,
    createLink: (projectId?: number | string) => `/api/v1/projects/${projectId}/links`,
    getRecommendations: (projectId?: number | string) =>
      `/api/v1/projects/${projectId}/inspectors-recommendations`,
    addAuditTypesWithActivities: (projectId: number | string) =>
      `/api/v1/projects/${projectId}/audit-types/with-activities`,
    auditTypes: (projectId: number | string) => `/api/v1/projects/${projectId}/audit-types`,
    updateAuditTypeAccreditationBody: (projectId: number | string, auditTypeId: number | string) =>
      `/api/v1/projects/${projectId}/audit-types/${auditTypeId}/accreditation-body`,
    updateAuditTypeOutcome: (projectId: number | string, auditTypeId: number | string) =>
      `/api/v1/projects/${projectId}/audit-types/${auditTypeId}/outcome`,
    notes: {
      list: (projectId?: number | string) => `/api/v1/projects/${projectId}/notes`,
      create: (projectId?: number | string) => `/api/v1/projects/${projectId}/notes`,
      update: (projectId?: number | string, noteId?: number | string) =>
        `/api/v1/projects/${projectId}/notes/${noteId}`,
      delete: (projectId?: number | string, noteId?: number | string) =>
        `/api/v1/projects/${projectId}/notes/${noteId}`,
    },
    activityStatusCounts: (projectId: number | string) =>
      `/api/v1/projects/${projectId}/activity-status-counts`,
    daysBooked: (projectId: number | string) => `/api/v1/projects/${projectId}/days-booked`,
    projectSiteContactPeople: (projectId: number | string, siteId: number | string) =>
      `/api/v1/projects/${projectId}/sites/${siteId}/contact-people`,
  },

  programmes: {
    root: '/api/v1/programmes',
    availability: '/api/v1/programmes/availability',
    eligibleSchemes: '/api/v1/programmes/eligible-schemes',
    details: (id: number | string) => `/api/v1/programmes/${id}`,
    years: (id: number | string) => `/api/v1/programmes/${id}/years`,
    byProject: (projectId: number | string) =>
      `/api/v1/programmes/by-project/${projectId}`,
    create: '/api/v1/programmes',
    update: (id: number | string) => `/api/v1/programmes/${id}`,
    submit: (id: number | string) => `/api/v1/programmes/${id}/submit`,
    reject: (id: number | string) => `/api/v1/programmes/${id}/reject`,
    approve: (id: number | string) => `/api/v1/programmes/${id}/approve`,
    complete: (id: number | string) => `/api/v1/programmes/${id}/complete`,
    changesSinceLastApproval: (id: number | string) =>
      `/api/v1/programmes/${id}/changes-since-last-approval`,
    issues: (id: number | string) => `/api/v1/programmes/${id}/issues`,
    issue: (id: number | string, issueNumber: number | string) =>
      `/api/v1/programmes/${id}/issues/${issueNumber}`,
    issueChangesSinceApproval: (id: number | string) =>
      `/api/v1/programmes/${id}/issues/changes-since-approval`,
  },

  activities: {
    root: '/api/v1/activities',
    list: '/api/v1/activities/list',
    details: (id: number | string) => `/api/v1/activities/${id}`,
    dependencySchedulingConstraints: (id: number | string) =>
      `/api/v1/activities/${id}/dependency-scheduling-constraints`,
    search: '/api/v1/activities/search',
    create: '/api/v1/activities',
    duplicate: (id: number | string) => `/api/v1/activities/${id}/duplicate`,
    update: '/api/v1/activities',
    patch: (id: number | string) => `/api/v1/activities/${id}`,
    delete: '/api/v1/activities',
    allocate: (planboxId: number | string) =>
      `/api/v1/activities/allocate-specific-async/${planboxId}`,
    unallocate: (id: number | string) => `/api/v1/activities/${id}/allocation`,
    allocateSingle: (id: number | string) => `/api/v1/activities/${id}/inspectors`,
    allocateUnscheduled: (activityId: number | string) =>
      `/api/v1/activity-inspectors/${activityId}`,
    // DEPRECATED: Removed byInspector endpoint - use root endpoint with inspector-ids parameter instead
    reschedule: '/api/v1/activities/reschedule',
    rescheduleSuggestions: (id: number) =>
      `/api/v1/activities/${id}/reschedule/suggestions`,
    rescheduleProgress: (rescheduleProgressId: number) =>
      `/api/v1/activities/reschedule-progress/${rescheduleProgressId}`,
    latestRescheduleProgress: (activityId: number) =>
      `/api/v1/activities/${activityId}/reschedule/progress`,
    cancelRescheduleProgress: (rescheduleProgressId: number) =>
      `/api/v1/activities/reschedule-progress/${rescheduleProgressId}/cancel`,
    deactivateRescheduleProgress: (rescheduleProgressId: number) =>
      `/api/v1/activities/reschedule-progress/${rescheduleProgressId}/deactivate`,
    status: '/api/v1/activities/status',
    byProject: (projectId: string) => `/api/v1/activities/project/${projectId}`,
    names: '/api/v1/activities/names',
    auditTrail: (id: number | string) => `/api/v1/activities/${id}/audit-trail`,
    updateLeadAuditor: (id: number | string) => `/api/v1/activities/${id}/lead-auditor`,
    competencies: (id: number | string) => `/api/v1/activities/${id}/competencies`,
    batchCompetencies: '/api/v1/activities/batch-add-competencies',
    batchRemoveCompetencies: '/api/v1/activities/batch-remove-competencies',
    batchExcludedDates: '/api/v1/activities/preferred-dates/batch',
    webhook: '/api/v1/activities/booking-request/cancellation',
    export: '/api/v1/activities/export',
    exportStatus: (exportId: number | string) =>
      `/api/v1/activities/exports/${exportId}`,
    exportDownload: (exportId: number | string) =>
      `/api/v1/activities/exports/${exportId}/download`,
    preferredDates: (activityId: number | string) =>
      `/api/v1/activities/${activityId}/preferred-dates`,
    preferredDate: (activityId: number | string, preferredDateId: number | string) =>
      `/api/v1/activities/${activityId}/preferred-dates/${preferredDateId}`,
    splitPartsPreview: '/api/v1/activities/split-parts-preview',
    splitParts: (id: number | string) => `/api/v1/activities/${id}/split-parts`,
  },
  planbox: {
    root: '/api/v1/planboxes',
    create: '/api/v1/planboxes',
    update: (id: number | string) => `/api/v1/planboxes/${id}`,
    removeActivity: (id: number | string, activityId: number | string) =>
      `/api/v1/planboxes/${id}/activities/${activityId}`,
    getProgress: (id: number) => `/api/v1/planboxes/${id}/progress`,
    latest: '/api/v1/planboxes/latest',
  },
  solutions: {
    create: (planboxId: string | number) => `/api/v1/solutions/${planboxId}`,
    list: (planboxId: string | number) => `/api/v1/solutions/planbox/${planboxId}`,
    details: (id: string | number) => `/api/v1/solutions/${id}`,
    activities: (id: string | number) => `/api/v1/solutions/${id}/activities`,
    updateAllocation: (solutionId: string | number, serviceId: string | number) =>
      `/api/v1/solutions/${solutionId}/allocated-services/${serviceId}`,
    updateAllocationUnallocated: (solutionId: string | number, serviceId: string | number) =>
      `/api/v1/solutions/${solutionId}/unallocated-services/${serviceId}`,
    allocationAuditTrail: (solutionId: string | number, activityId: string | number) =>
      `/api/v1/solutions/${solutionId}/activities/${activityId}/allocation-audit-trail`,
  },
  inspectors: {
    suitableInspectors: (
      activityId: string | number,
      month?: string,
      onlyInternal?: boolean,
      preferredDay?: string
    ) =>
      `/api/v1/inspectors/activity/${activityId}/suitable-inspectors?targetMonth=${month}&onlyInternal=${onlyInternal}&preferredDay=${preferredDay}`,
    suitableInspectorsByActivityId: (activityId: string | number) =>
      `/api/v1/inspectors/activity/${activityId}/suitable-inspectors`,
    earliestAvailabilityByActivityId: (activityId: string | number) =>
      `/api/v1/inspectors/activity/${activityId}/earliest-availability`,
    /**
     * @deprecated Use `base` instead.
     * You shouldn't use this endpoint anymore as it's deprecated because it's not following RESTful conventions.
     * Instead, use the `base` endpoint.
     */
    list: '/api/v1/inspectors/list',
    base: '/api/v1/inspectors',
    details: '/api/v1/inspectors',
    create: '/api/v1/inspectors',
    update: '/api/v1/inspectors',
    delete: '/api/v1/inspectors',
    byTeamId: '/api/v1/inspectors/team',
    unassigned: '/api/v1/inspectors/unassigned',
    names: '/api/v1/inspectors/names',
    countries: '/api/v1/inspectors/countries',
    invite: (id: string) => `/api/v1/inspectors/${id}/invite`,
    filterOptions: '/api/v1/inspectors/filter-options',
    accreditationBodiesForFilter: '/api/v1/inspectors/accreditation-bodies',
    getInspector: (id: string | number) => `${endpoints.inspectors.base}/${id}`,
    getUnavailability: `/api/v1/inspectors/unavailability`,
    export: '/api/v1/inspectors/export',
    exportDownload: (exportId: number) => `/api/v1/inspectors/exports/${exportId}/download`,
  },
  appointments: {
    root: '/api/v1/appointments',
    approve: '/api/v1/appointments/approve-and-create',
    create: '/api/v1/appointments',
    delete: (id: number | string) => `/api/v1/appointments/${id}`,
    update: (id: number | string) => `/api/v1/appointments/${id}`,
    notifyAppointment: '/api/v1/appointments/notify-appointment',
    notifyActivity: '/api/v1/appointments/notify-activity',
    notifyInspector: '/api/v1/appointments/notify-inspector',
    split: '/api/v1/appointments/split',
    splitDurationPreview: '/api/v1/appointments/split-duration-preview',
    combine: '/api/v1/appointments/combine',
    activityAppointments: (activityId: number | string) => `/api/v1/service/${activityId}`,
    updateProjectRole: (id: number | string) => `/api/v1/appointments/${id}/project-role`,
    generateFeedbacks: '/api/v1/appointments/generate-feedback',
  },
  authorizations: {
    list: '/api/v1/authorizations/list',
    create: '/api/v1/authorizations',
    edit: '/api/v1/authorizations',
    delete: '/api/v1/authorizations',
  },
  personnelPreferrence: {
    base: '/api/v1/personnel-preferrence',
    list: '/api/v1/personnel-preferrence/list',
    create: '/api/v1/personnel-preferrence',
    edit: '/api/v1/personnel-preferrence',
    update: '/api/v1/personnel-preferrence',
    delete: '/api/v1/personnel-preferrence',
  },
  team: {
    list: '/api/v1/teams/list',
    details: '/api/v1/teams',
    create: '/api/v1/teams',
    update: '/api/v1/teams',
    delete: '/api/v1/teams',
  },
  region: {
    list: '/api/v1/regions/list',
    details: '/api/v1/regions',
    create: '/api/v1/regions',
    update: '/api/v1/regions',
    delete: '/api/v1/regions',
  },
  standardCategories: {
    list: '/api/v1/standard-category/list',
    details: '/api/v1/standard-category',
    search: '/api/v1/standard-category/search',
    create: '/api/v1/standard-category',
    update: '/api/v1/standard-category',
    delete: '/api/v1/standard-category',
  },
  clientTypes: {
    list: '/api/v1/client-types/list',
    details: '/api/v1/client-types',
    search: '/api/v1/client-types/search',
    create: '/api/v1/client-types',
    update: '/api/v1/client-types',
    delete: '/api/v1/client-types',
  },
  memberRoles: {
    root: `/api/v1/member-roles`,
    list: '/api/v1/member-roles/list',
    details: '/api/v1/member-roles',
    search: '/api/v1/member-roles/search',
    create: '/api/v1/member-roles',
    update: '/api/v1/member-roles',
    delete: '/api/v1/member-roles',
  },
  organizationalRoles: {
    list: '/api/v1/organizational-roles',
    details: (id: number | string) => `/api/v1/organizational-roles/${id}`,
    create: '/api/v1/organizational-roles',
    update: (id: number | string) => `/api/v1/organizational-roles/${id}`,
    delete: (id: number | string) => `/api/v1/organizational-roles/${id}`,
    reassignUsers: (id: number | string, newRoleId: number | string) =>
      `/api/v1/organizational-roles/${id}/reassign-users/${newRoleId}`,
    usersCount: (id: number | string) => `/api/v1/organizational-roles/${id}/users-count`,
  },
  countryWorkingDays: {
    list: '/api/v1/country-working-days',
    details: (id: number | string) => `/api/v1/country-working-days/${id}`,
  },
  holidays: {
    list: '/api/v1/holidays',
    details: '/api/v1/holidays',
    create: '/api/v1/holidays',
    update: '/api/v1/holidays',
    delete: '/api/v1/holidays',
    libraryCountries: '/api/v1/holidays/library/countries',
    libraryPreview: '/api/v1/holidays/library/preview',
    import: '/api/v1/holidays/import',
  },
  businessUnits: {
    list: '/api/v1/business-units',
    details: (id: number | string) => `/api/v1/business-units/${id}`,
    create: '/api/v1/business-units',
    update: (id: number | string) => `/api/v1/business-units/${id}`,
    delete: (id: number | string) => `/api/v1/business-units/${id}`,
    managers: (id: number | string) => `/api/v1/business-units/${id}/managers`,
    updateManagersOrder: (id: number | string) => `/api/v1/business-units/${id}/managers/order`,
  },
  projectRoles: {
    list: '/api/v1/project-roles',
    details: (id: number | string) => `/api/v1/project-roles/${id}`,
    create: '/api/v1/project-roles',
    update: (id: number | string) => `/api/v1/project-roles/${id}`,
    delete: (id: number | string) => `/api/v1/project-roles/${id}`,
    eligibleForInspectors: '/api/v1/project-roles/eligible-for-inspectors',
    suggestForInspectors: '/api/v1/project-roles/suggest-for-inspectors',
  },
  reports: {
    utilization: '/api/v1/reports/utilization',
    compliance: '/api/v1/reports/compliance',
    travel: '/api/v1/reports/travel',
    complianceExport: '/api/v1/reports/compliance/export',
    travelExport: '/api/v1/reports/travel/export',
    appointmentsSummary: '/api/v1/reports/appointments/summary',
    serviceLevel: '/api/v1/reports/serviceLevel',
    activities: '/api/v1/reports/activity',
    appointmentsTravel: '/api/v1/reports/appointments/travel',
  },
  fileImport: {
    list: '/api/v1/file-imports',
    details: (id: number) => `/api/v1/file-imports/${id}`,
    delete: (id: number) => `/api/v1/file-imports/${id}`,
    validate: '/api/v1/file-imports/validate',
    template: '/api/v1/file-imports/template',
    status: (id: number) => `/api/v1/file-imports/${id}/status`,
    uploadInspectors: '/api/v1/file-imports/upload/inspectors',
    uploadProjects: '/api/v1/file-imports/upload/projects',
    uploadSchemes: '/api/v1/file-imports/upload/schemes',
    uploadCustomers: '/api/v1/file-imports/upload/customers',
  },
  fileManager: {
    uploadUrl: '/api/v1/et-file-manager/upload-url',
    completeUpload: '/api/v1/et-file-manager/complete-upload',
  },
  clients: {
    base: '/api/v1/clients',
    list: (clientId: number) => `/api/v1/clients/${clientId}`,
    update: (clientId: number) => `/api/v1/clients/${clientId}`,
    sso: (clientId: number) => `/api/v1/clients/${clientId}/sso`,
    ssoProvider: (clientId: number, providerId: string) =>
      `/api/v1/clients/${clientId}/sso/providers/${providerId}`,
    ssoConnection: (clientId: number, connectionId: number) =>
      `/api/v1/clients/${clientId}/sso/connections/${connectionId}`,
    ssoDomains: (clientId: number) => `/api/v1/clients/${clientId}/sso/domains`,
    ssoDomain: (clientId: number, domainId: number) =>
      `/api/v1/clients/${clientId}/sso/domains/${domainId}`,
    ssoDomainVerify: (clientId: number, domainId: number) =>
      `/api/v1/clients/${clientId}/sso/domains/${domainId}/verify`,
    users: {
      list: (clientId: number) => `/api/v1/clients/${clientId}/users`,
      countries: (clientId: number) => `/api/v1/clients/${clientId}/users/countries`,
      get: (clientId: number, userId: number) => `/api/v1/clients/${clientId}/users/${userId}`,
      create: (clientId: number) => `/api/v1/clients/${clientId}/users`,
      update: (clientId: number, userId: number) => `/api/v1/clients/${clientId}/users/${userId}`,
      delete: (clientId: number, userId: number) => `/api/v1/clients/${clientId}/users/${userId}`,
      resendInvitation: (clientId: number, userId: number) =>
        `/api/v1/clients/${clientId}/users/${userId}/resend-invitation`,
      suspend: (clientId: number, userId: number) =>
        `/api/v1/clients/${clientId}/users/${userId}/suspend`,
      activate: (clientId: number, userId: number) =>
        `/api/v1/clients/${clientId}/users/${userId}/activate`,
      updateMfaFactor: (clientId: number, userId: number) =>
        `/api/v1/clients/${clientId}/users/${userId}/mfa/factors`,
      resetAllMfa: (clientId: number, userId: number) =>
        `/api/v1/clients/${clientId}/users/${userId}/mfa/reset-all`,
    },
  },
  users: {
    root: '/api/v1/users',
    taskAssignees: '/api/v1/users/task-assignees',
    update: (userId: number) => `/api/v1/users/${userId}`,
  },
  auditTrails: {
    get: '/api/v1/audit-trail',
    getEventTypes: '/api/v1/audit-trail/event-types',
    getEntityTypes: '/api/v1/audit-trail/entity-types',
  },
  tasks: {
    root: '/api/v1/tasks',
    list: '/api/v1/tasks/list',
    search: '/api/v1/tasks/search',
    create: '/api/v1/tasks',
    details: (id: number | string) => `/api/v1/tasks/${id}`,
    update: (id: number) => `/api/v1/tasks/${id}`,
    delete: (id: number) => `/api/v1/tasks/${id}`,
    filterOptions: '/api/v1/tasks/filter-options',
    names: '/api/v1/tasks/names',
  },
  notifications: {
    list: '/api/v1/notifications',
    markAsRead: (id: string | number) => `/api/v1/notifications/${id}/read`,
    markAllAsRead: '/api/v1/notifications/read',
  },
  savedViews: {
    root: '/api/v1/saved-views',
  },
  emailTemplates: {
    list: '/api/v1/email-templates',
    get: (id: number) => `/api/v1/email-templates/${id}`,
    create: '/api/v1/email-templates',
    update: (id: number) => `/api/v1/email-templates/${id}`,
    delete: (id: number) => `/api/v1/email-templates/${id}`,
    createContent: (id: number) => `/api/v1/email-templates/${id}/contents`,
    updateContent: (id: number, languageCode: string) =>
      `/api/v1/email-templates/${id}/contents/${encodeURIComponent(languageCode)}`,
    deleteContent: (id: number, languageCode: string) =>
      `/api/v1/email-templates/${id}/contents/${encodeURIComponent(languageCode)}`,
    sendTest: (id: number) => `/api/v1/email-templates/${id}/send-test`,
    preview: (id: number) => `/api/v1/email-templates/${id}/preview`,
    activate: (id: number) => `/api/v1/email-templates/${id}/activate`,
  },
  emailConfiguration: {
    get: '/api/v1/email-configuration',
    update: '/api/v1/email-configuration',
    sendTestEmail: '/api/v1/email-configuration/test-email',
    outlook: {
      connect: '/api/v1/email-configuration/outlook/connect',
      disconnect: '/api/v1/email-configuration/outlook/disconnect',
      connection: '/api/v1/email-configuration/outlook/connection',
      callback: '/api/v1/email-configuration/outlook/callback',
    },
  },
  webhooks: {
    list: '/api/v1/webhooks',
    details: (id: number | string) => `/api/v1/webhooks/${id}`,
    create: '/api/v1/webhooks',
    update: (id: number | string) => `/api/v1/webhooks/${id}`,
    delete: (id: number | string) => `/api/v1/webhooks/${id}`,
    logs: '/api/v1/webhooks/logs',
    bookingRequestExists: '/api/v1/webhooks/booking-request/exists',
    actions: {
      create: (webhookId: number | string) => `/api/v1/webhooks/${webhookId}/actions`,
      update: (webhookId: number | string, actionId: number | string) =>
        `/api/v1/webhooks/${webhookId}/actions/${actionId}`,
      delete: (webhookId: number | string, actionId: number | string) =>
        `/api/v1/webhooks/${webhookId}/actions/${actionId}`,
    },
  },
  search: {
    universal: '/api/v1/search',
  },
  emailOutbox: {
    list: '/api/v1/email-outbox',
    details: (id: number | string) => `/api/v1/email-outbox/${id}`,
    approve: (id: number | string) => `/api/v1/email-outbox/${id}/approve`,
    skip: (id: number | string) => `/api/v1/email-outbox/${id}/skip`,
    resend: (id: number | string) => `/api/v1/email-outbox/${id}/resend`,
  },
  auditTypes: {
    // Client-level endpoint (derives clientId from JWT token)
    clientList: () => `/api/v1/audit-types`,
    // Scheme-scoped endpoints
    list: (schemeId: number | string) => `/api/v1/standard-template/${schemeId}/audit-types`,
    create: (schemeId: number | string) => `/api/v1/standard-template/${schemeId}/audit-types`,
    update: (schemeId: number | string, auditTypeId: number | string) =>
      `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}`,
    delete: (schemeId: number | string, auditTypeId: number | string) =>
      `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}`,
    activities: {
      list: (schemeId: number | string, auditTypeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/activities`,
      create: (schemeId: number | string, auditTypeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/activities`,
      update: (
        schemeId: number | string,
        auditTypeId: number | string,
        activityId: number | string
      ) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/activities/${activityId}`,
      delete: (
        schemeId: number | string,
        auditTypeId: number | string,
        activityId: number | string
      ) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/activities/${activityId}`,
      reorder: (schemeId: number | string, auditTypeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/activities/reorder`,
    },
    windows: {
      list: (schemeId: number | string, auditTypeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/windows`,
      create: (schemeId: number | string, auditTypeId: number | string) =>
        `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/windows`,
      update: (
        schemeId: number | string,
        auditTypeId: number | string,
        windowId: number | string
      ) => `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/windows/${windowId}`,
      delete: (
        schemeId: number | string,
        auditTypeId: number | string,
        windowId: number | string
      ) => `/api/v1/standard-template/${schemeId}/audit-types/${auditTypeId}/windows/${windowId}`,
    },
  },
  programmeYears: {
    list: (schemeId: number | string) =>
      `/api/v1/standard-template/${schemeId}/programme-years`,
    create: (schemeId: number | string) =>
      `/api/v1/standard-template/${schemeId}/programme-years`,
    update: (schemeId: number | string, yearId: number | string) =>
      `/api/v1/standard-template/${schemeId}/programme-years/${yearId}`,
    delete: (schemeId: number | string, yearId: number | string) =>
      `/api/v1/standard-template/${schemeId}/programme-years/${yearId}`,
  },
  tenantConnection: {
    // Client-scoped connection status (backend TenantConnectionController). The package-level
    // /auth/microsoft/tenant/connection is NOT client-scoped — in multi-tenant mode it returns the
    // first active tenant globally, so a second client on a different tenant read the wrong tenant
    // (or none) and showed "Not connected" despite a successful connect.
    get: '/api/v1/calendar/tenant/connection',
    adminConsentUrl: '/api/v1/auth/microsoft/tenant/admin-consent',
    // Package-level disconnect (DELETE), served by @checkfirst/nestjs-outlook's TenantAuthController.
    disconnect: '/api/v1/auth/microsoft/tenant/connection',
    // Backend calendar controller: records the client↔tenant link and drives bulk connect.
    consentInit: '/api/v1/calendar/tenant/consent-init',
    link: '/api/v1/calendar/tenant/link',
    // Same path as `link`, distinguished by HTTP method (GET): the ScheduleAI client-scoped link
    // status the dashboard drives its "connected" state from.
    linkStatus: '/api/v1/calendar/tenant/link',
    connected: '/api/v1/calendar/tenant/connected',
    unlinkLink: '/api/v1/calendar/tenant/link',
    connectAll: '/api/v1/calendar/tenant/connect-all',
    // Progress of a bulk connect-all run: latest header, or a specific run with per-auditor items.
    connectAllLatest: '/api/v1/calendar/tenant/connect-all/latest',
    connectAllRun: (runId: number | string) =>
      `/api/v1/calendar/tenant/connect-all/${runId}`,
    users: '/api/v1/calendar/tenant/users',
    inspectorDisconnect: (id: number | string) =>
      `/api/v1/inspectors/${id}/outlook-connection`,
    inspectorConnect: (id: number | string) =>
      `/api/v1/inspectors/${id}/outlook-connection`,
  },
  geocoding: {
    detectCountry: '/api/v1/geocoding/detect-country',
  },
};
